import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { setCookie } from '../../services/Cookies';
import apiService from '../../services/ApiService';
import type { FMEStatusBarProps } from './FMEStatusBar';
import { getComputeErrors, getSensitivityComputeErrors } from '../../shared/api/scenarios';
import { AGENT_DATA_CHANGED_EVENT } from '../../hooks/useAgentChat';
import type { AgentDataChange } from '../../hooks/useAgentChat';
import { useComputeStore } from '../../shared/stores/useComputeStore';
import { useEditorStore } from '../../shared/stores/useEditorStore';

interface ModelContextType {
    model: any;
    currentScenario: any;
    currentSensitivity: any;
    currentEntity: any;
    setCurrentScenario: (scenario: any) => void;
    setCurrentSensitivity: (sensitivity: any) => void;
    refreshModel: () => Promise<void>;
    statusBarProps: FMEStatusBarProps;
    setStatusBarProps: (props: FMEStatusBarProps) => void;
    // Entity resolution state — true while async fetch is in progress (direct URL / page reload)
    isEntityLoading: boolean;
    entityError: string | null;
    // WebSocket — shared across all FME sections
    sendJsonMessage: (msg: any, keep?: boolean) => void;
    lastJsonMessage: any;
    readyState: ReadyState;
    getWebSocket: () => WebSocket | null;
    // Compute state — updated from WebSocket events, always alive
    isComputing: boolean | undefined;
    isComputed: boolean | undefined;
    auditPassed: boolean | null;
    computeFailedRef: React.MutableRefObject<boolean>;
    // Incremented every time a compute result or data-modifying change is received
    computeGeneration: number;
    // Compute errors — fetched from API, available on all pages
    contextFirstError: string | null;
    contextSensitivityErrorCount: number;
}

const ModelContext = createContext<ModelContextType | null>(null);

export function useModelContext(): ModelContextType {
    const context = useContext(ModelContext);
    if (!context) {
        throw new Error('useModelContext must be used within a ModelContextProvider');
    }
    return context;
}

export function useModelContextSafe(): ModelContextType | null {
    return useContext(ModelContext);
}

// Extract model ID from any entity shape (scenario entity, sensitivity entity, or raw model)
function extractModelId(entity: any): number | null {
    if (!entity) return null;
    if (entity?.previous_entity?.id) return entity.previous_entity.id;
    if (entity?.model_id) return entity.model_id;
    // If entity itself is a model (has modelScenarios or model_scenarios)
    if (entity?.modelScenarios || entity?.model_scenarios) return entity.id;
    return null;
}

// Extract initial scenario ID from entity
function extractScenarioId(entity: any): number | null {
    if (!entity) return null;
    if (entity.type === 'scenario') return entity.id;
    if (entity.type === 'sensitivity') return entity.scenario_id;
    // If entity is a raw model, use first scenario
    if (typeof entity.currentScenario === 'number') return entity.currentScenario;
    if (entity.currentScenario?.id) return entity.currentScenario.id;
    const scenarios = entity.modelScenarios || entity.model_scenarios || [];
    if (scenarios.length > 0) {
        return typeof scenarios[0] === 'number' ? scenarios[0] : scenarios[0]?.id || null;
    }
    return null;
}

// Extract initial sensitivity ID from entity
function extractSensitivityId(entity: any): number | null {
    if (!entity) return null;
    if (entity.type === 'sensitivity') return entity.id;
    return null;
}

export function ModelContextProvider({ children, initialEntity, initialEntityId }: {
    children: React.ReactNode;
    initialEntity: any;
    // ID de l'entité URL quand l'entité complète n'est pas disponible (ex: rechargement, URL directe)
    initialEntityId?: number | null;
}) {
    const modelIdFromEntity = useMemo(() => extractModelId(initialEntity), [initialEntity]);

    // Modèle résolu via l'ID URL quand l'entité n'est pas dispo (résolution asynchrone)
    const [resolvedModelId, setResolvedModelId] = useState<number | null>(null);
    const [isEntityLoading, setIsEntityLoading] = useState<boolean>(!initialEntity && !!initialEntityId);
    const [entityError, setEntityError] = useState<string | null>(null);

    // modelId : priorité à l'entité, sinon modèle résolu via API
    const modelId = modelIdFromEntity || resolvedModelId;

    // Track selections as simple IDs
    const [selectedScenarioId, setSelectedScenarioId] = useState<number | null>(
        () => extractScenarioId(initialEntity)
    );
    const [selectedSensitivityId, setSelectedSensitivityId] = useState<number | null>(
        () => extractSensitivityId(initialEntity)
    );

    // Resolve model from URL entity ID when full entity is not available
    // (cases: page reload, direct URL navigation, bookmark)
    useEffect(() => {
        if (modelIdFromEntity || !initialEntityId) return;

        setIsEntityLoading(true);
        setEntityError(null);

        const resolveAsScenario = async (id: number) => {
            try {
                const response = await apiService.getModelDataByScenario(id, false);
                if (response && response.ok) {
                    const data = await response.json();
                    if (data.model_id) {
                        setResolvedModelId(data.model_id);
                        setSelectedScenarioId(id);
                        setIsEntityLoading(false);
                        return;
                    }
                }
            } catch {
                // Not a valid scenario ID either
            }
            setEntityError('Model not found');
            setIsEntityLoading(false);
        };

        // Try as model ID first — URLs use model IDs from navigation.
        // Only fall back to scenario ID if the model lookup fails.
        apiService.getModel(initialEntityId, false).then(async (response) => {
            if (response && response.ok) {
                const data = await response.json();
                if (data.id) {
                    setResolvedModelId(data.id);
                    const scenarios = data.model_scenarios || data.modelScenarios || [];
                    if (scenarios.length > 0) {
                        setSelectedScenarioId(scenarios[0].id);
                    }
                    setIsEntityLoading(false);
                    return;
                }
            }
            // Entity ID is not a valid model — try as a scenario ID
            resolveAsScenario(initialEntityId);
        }).catch(() => {
            // Entity ID is not a valid model — try as a scenario ID
            resolveAsScenario(initialEntityId);
        });
    }, [modelIdFromEntity, initialEntityId]);

    // Model data from API — single source of truth
    const [modelData, setModelData] = useState<any>(null);

    // Fetch full model from GET /model/{id}
    const refreshModel = useCallback(async () => {
        if (!modelId) return;
        try {
            const response = await apiService.getModel(modelId as number);
            if (response) {
                const resp = await response.json();
                setModelData({
                    id: resp.id,
                    name: resp.name,
                    // API returns snake_case; normalize to camelCase
                    modelScenarios: resp.model_scenarios || resp.modelScenarios || [],
                    modelRulesDeducted: resp.model_rules_deducted || resp.modelRulesDeducted,
                });
            }
        } catch (e) {
            console.error('Error fetching model:', e);
        }
    }, [modelId]);

    useEffect(() => { refreshModel(); }, [refreshModel]);

    // ── Fetch user rights and populate Zustand store (needed by all pages) ──
    useEffect(() => {
        if (!modelId || !modelData) return;
        apiService.getUserRightsSharedModels(modelId as number).then(async (r) => {
            if (r?.ok) {
                const rights = await r.json();
                useEditorStore.getState().setUserRights({
                    user_id: rights.user_id,
                    right_to_share: rights.right_to_share,
                    right_to_read: rights.right_to_read,
                    right_to_write: modelData.modelRulesDeducted?.is_active !== false && rights.right_to_write,
                    right_to_duplicate: rights.right_to_duplicate,
                });
            }
        }).catch((err) => console.error('Failed to fetch user rights:', err));
    }, [modelId, modelData?.modelRulesDeducted?.is_active]);

    // Build model object (API data or fallback while loading)
    const model = useMemo(() => {
        if (modelData) return modelData;
        // Fallback depuis l'entité initiale pendant le chargement
        return { id: modelId || 0, name: initialEntity?.modelName || initialEntity?.name || 'Loading...', modelScenarios: [] };
    }, [modelData, modelId, initialEntity]);

    // Derive current scenario from API data
    const currentScenario = useMemo(() => {
        const scenarios = model.modelScenarios || [];
        if (!scenarios.length) return null;
        return scenarios.find((s: any) => s.id === selectedScenarioId) || scenarios[0];
    }, [model.modelScenarios, selectedScenarioId]);

    // Derive current sensitivity from current scenario's sensitivities
    const currentSensitivity = useMemo(() => {
        if (!selectedSensitivityId || !currentScenario?.sensitivities) return null;
        return currentScenario.sensitivities.find((s: any) => s.id === selectedSensitivityId) || null;
    }, [selectedSensitivityId, currentScenario]);

    // Build currentEntity object compatible with page data-fetching patterns
    const currentEntity = useMemo(() => {
        if (!currentScenario) return initialEntity; // Fallback while API loads
        if (currentSensitivity) {
            return {
                type: 'sensitivity',
                id: currentSensitivity.id,
                name: currentSensitivity.name,
                scenario_id: currentScenario.id,
                previous_entity: model,
            };
        }
        return {
            type: 'scenario',
            id: currentScenario.id,
            name: currentScenario.name,
            previous_entity: model,
        };
    }, [currentScenario, currentSensitivity, model, initialEntity]);

    // Persist entity to cookie whenever it changes
    useEffect(() => {
        if (currentEntity && modelData) {
            setCookie('entity', JSON.stringify({
                ...currentEntity,
                modelName: model.name,
            }));
        }
    }, [currentEntity, modelData, model.name]);

    // Listen for agent data_changed events and apply structural updates
    useEffect(() => {
        const handler = (e: Event) => {
            const change = (e as CustomEvent<AgentDataChange>).detail;

            switch (change.type) {
                case 'scenario_created': {
                    const scenario = change.scenario as any;
                    if (scenario && change.model_id === modelId) {
                        setModelData((prev: any) => {
                            if (!prev) return prev;
                            const exists = prev.modelScenarios.some((s: any) => s.id === scenario.id);
                            if (exists) return prev;
                            return {
                                ...prev,
                                modelScenarios: [...prev.modelScenarios, scenario],
                            };
                        });
                        // Auto-select the newly created scenario
                        setSelectedScenarioId(scenario.id);
                        setSelectedSensitivityId(null);
                    }
                    break;
                }
                case 'scenario_deleted': {
                    if (change.model_id === modelId) {
                        setModelData((prev: any) => {
                            if (!prev) return prev;
                            return {
                                ...prev,
                                modelScenarios: prev.modelScenarios.filter(
                                    (s: any) => s.id !== change.scenario_id
                                ),
                            };
                        });
                        // If the deleted scenario was selected, switch to the first available
                        if (selectedScenarioId === change.scenario_id) {
                            setSelectedScenarioId(null);
                        }
                    }
                    break;
                }
                case 'scenario_renamed': {
                    if (change.model_id === modelId) {
                        setModelData((prev: any) => {
                            if (!prev) return prev;
                            return {
                                ...prev,
                                modelScenarios: prev.modelScenarios.map((s: any) =>
                                    s.id === change.scenario_id
                                        ? { ...s, name: change.new_name }
                                        : s
                                ),
                            };
                        });
                    }
                    break;
                }
                case 'sensitivity_created': {
                    const sensitivity = change.sensitivity as any;
                    if (sensitivity) {
                        setModelData((prev: any) => {
                            if (!prev) return prev;
                            return {
                                ...prev,
                                modelScenarios: prev.modelScenarios.map((s: any) => {
                                    if (s.id !== change.scenario_id) return s;
                                    const exists = s.sensitivities?.some(
                                        (sens: any) => sens.id === sensitivity.id
                                    );
                                    if (exists) return s;
                                    return {
                                        ...s,
                                        sensitivities: [...(s.sensitivities || []), sensitivity],
                                    };
                                }),
                            };
                        });
                        // Auto-select the newly created sensitivity (and its parent scenario)
                        setSelectedScenarioId(change.scenario_id as number);
                        setSelectedSensitivityId(sensitivity.id);
                    }
                    break;
                }
                case 'sensitivity_deleted': {
                    setModelData((prev: any) => {
                        if (!prev) return prev;
                        return {
                            ...prev,
                            modelScenarios: prev.modelScenarios.map((s: any) => {
                                if (s.id !== change.scenario_id) return s;
                                return {
                                    ...s,
                                    sensitivities: (s.sensitivities || []).filter(
                                        (sens: any) => sens.id !== change.sensitivity_id
                                    ),
                                };
                            }),
                        };
                    });
                    if (selectedSensitivityId === change.sensitivity_id) {
                        setSelectedSensitivityId(null);
                    }
                    break;
                }
                case 'sensitivity_renamed': {
                    setModelData((prev: any) => {
                        if (!prev) return prev;
                        return {
                            ...prev,
                            modelScenarios: prev.modelScenarios.map((s: any) => {
                                if (s.id !== change.scenario_id) return s;
                                return {
                                    ...s,
                                    sensitivities: (s.sensitivities || []).map((sens: any) =>
                                        sens.id === change.sensitivity_id
                                            ? { ...sens, name: change.new_name }
                                            : sens
                                    ),
                                };
                            }),
                        };
                    });
                    break;
                }
            }

            // Increment generation for any data-modifying change so non-FME pages can react
            if (['field_value_updated', 'field_reload', 'block_created', 'block_deleted', 'block_renamed',
                 'sensitivity_field_updated', 'sensitivity_field_reload'].includes(change.type) && change.scenario_id) {
                setComputeGeneration(g => g + 1);
                useComputeStore.getState().incrementGeneration();
            }
        };

        window.addEventListener(AGENT_DATA_CHANGED_EVENT, handler);
        return () => window.removeEventListener(AGENT_DATA_CHANGED_EVENT, handler);
    }, [modelId, selectedScenarioId, selectedSensitivityId]);

    // ── WebSocket — shared across all FME sections ─────────────────────
    const socketUrl = currentScenario?.id ? '/scenario_events/' + currentScenario.id : null;
    // A function, not a string: react-use-websocket calls it again on every reconnect,
    // so a reconnect never reuses an expired or rotated token.
    const getSocketUrl = useCallback(() => apiService.getWebSocketUrl(socketUrl!), [socketUrl]);
    const { sendJsonMessage, lastJsonMessage, readyState, getWebSocket } = useWebSocket(
        socketUrl ? getSocketUrl : null,
        {
            shouldReconnect: (_closeEvent: any) => true,
            reconnectAttempts: Infinity,
            reconnectInterval: (attemptNumber: number) =>
                Math.min(Math.pow(2, attemptNumber) * 1000, 10000),
        },
    );

    // ── Compute state — updated from WebSocket events ──────────────────
    const [isComputing, setIsComputing] = useState<boolean | undefined>(undefined);
    const [isComputed, setIsComputed] = useState<boolean | undefined>(undefined);
    const [auditPassed, setAuditPassed] = useState<boolean | null>(null);
    const computeFailedRef = useRef(false);
    const [computeGeneration, setComputeGeneration] = useState(0);

    // Track the latest compute_key seen for this scenario / sensitivity view.
    // The backend mints a fresh UUID on every trigger; downstream events carry
    // it so we can silently drop events from a compute that a later trigger
    // has already superseded. Using refs (not state) so the guard closure
    // always sees the most recent value without re-subscribing the WS handler.
    const currentScenarioComputeKey = useRef<string | null>(null);
    const currentSensitivityComputeKey = useRef<string | null>(null);

    const isStaleScenarioEvent = useCallback((msg: any): boolean => {
        if (!msg.compute_key) return false;  // no key → legacy event, accept
        const live = currentScenarioComputeKey.current;
        return live != null && msg.compute_key !== live;
    }, []);

    const isStaleSensitivityEvent = useCallback((msg: any): boolean => {
        if (!msg.compute_key) return false;
        const live = currentSensitivityComputeKey.current;
        return live != null && msg.compute_key !== live;
    }, []);

    // On tab visibility change: force reconnect if socket is closed
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && readyState !== WebSocket.OPEN) {
                getWebSocket()?.close(); // Force reconnect

                if (!currentSensitivity) {
                    sendJsonMessage({'type': 'scenario_state_request'});
                } else {
                    sendJsonMessage({'type': 'sensitivity_state_request', 'sensitivity_id': currentSensitivity.id});
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [readyState, getWebSocket, currentSensitivity, sendJsonMessage]);

    // On every WebSocket (re)connect, request current compute state from server
    const prevReadyStateRef = useRef<ReadyState | null>(null);
    useEffect(() => {
        if (readyState === ReadyState.OPEN && prevReadyStateRef.current !== ReadyState.OPEN) {
            if (!currentSensitivity) {
                sendJsonMessage({'type': 'scenario_state_request'});
            } else {
                sendJsonMessage({'type': 'sensitivity_state_request', 'sensitivity_id': currentSensitivity.id});
            }
        }
        prevReadyStateRef.current = readyState;
    }, [readyState, currentSensitivity, sendJsonMessage]);

    // When sensitivity changes, request the correct state from the server
    useEffect(() => {
        if (readyState !== ReadyState.OPEN) return;
        if (currentSensitivity) {
            sendJsonMessage({'type': 'sensitivity_state_request', 'sensitivity_id': currentSensitivity.id});
        } else {
            sendJsonMessage({'type': 'scenario_state_request'});
        }
    }, [currentSensitivity?.id]);

    // Handle compute-state WebSocket events (always alive across all sections)
    // WebSocket is notification-only — frontend pulls data via REST after receiving events.
    useEffect(() => {
        if (!lastJsonMessage) return;
        const msg = lastJsonMessage as any;
        const computeStore = useComputeStore.getState();

        // ── scenario_state (authoritative server state on reconnect) ──
        if (msg.type === 'scenario_state' && !currentSensitivity) {
            const serverIsComputing: boolean = msg.scenario?.is_computing;
            const serverIsComputed: boolean = msg.scenario?.is_computed;
            const serverAuditPassed: boolean | null = msg.scenario?.audit_passed ?? null;
            const serverGen: number = msg.compute_generation ?? 0;
            const localGen = computeStore.computeGeneration;
            setIsComputing(serverIsComputing);
            setAuditPassed(serverAuditPassed);
            computeStore.setComputeState({ isComputing: serverIsComputing, auditPassed: serverAuditPassed });
            // Detect missed compute result during disconnection
            if (!serverIsComputing && serverIsComputed && serverGen > localGen) {
                setIsComputed(true);
                computeStore.setComputeState({ isComputing: false, isComputed: true, computeError: null });
                computeStore.incrementGeneration();
                setComputeGeneration(g => g + 1);
                refreshModel();
            }
            if (!serverIsComputing && !serverIsComputed) {
                computeStore.setComputeState({ computeError: 'incomplete' });
            }
        }

        // ── sensitivity_state ──
        if (msg.type === 'sensitivity_state' && currentSensitivity && currentSensitivity.id == msg.sensitivity?.id) {
            const serverIsComputing: boolean = msg.sensitivity?.is_computing;
            const serverIsComputed: boolean = msg.sensitivity?.is_computed;
            const serverAuditPassed: boolean | null = msg.sensitivity?.audit_passed ?? null;
            const serverGen: number = msg.compute_generation ?? 0;
            const localGen = computeStore.computeGeneration;
            setIsComputing(serverIsComputing);
            setAuditPassed(serverAuditPassed);
            computeStore.setComputeState({ isComputing: serverIsComputing, auditPassed: serverAuditPassed });
            if (!serverIsComputing && serverIsComputed && serverGen > localGen) {
                setIsComputed(true);
                computeStore.setComputeState({ isComputing: false, isComputed: true, computeError: null });
                computeStore.incrementGeneration();
                setComputeGeneration(g => g + 1);
                refreshModel();
            }
            if (!serverIsComputing && !serverIsComputed) {
                computeStore.setComputeState({ computeError: 'incomplete' });
            }
        }

        // ── scenario_currently_computing ──
        // Latches the latest compute_key; any downstream event carrying an
        // older key is dropped by the guards below.
        if (msg.type === 'scenario_currently_computing') {
            if (msg.compute_key) {
                currentScenarioComputeKey.current = msg.compute_key;
            }
            computeFailedRef.current = false;
            setIsComputing(true);
            setAuditPassed(null);
            computeStore.setComputeState({ isComputing: true, auditPassed: null, computeError: null });
        }

        // ── sensitivity_currently_computing ──
        if (msg.type === 'sensitivity_currently_computing' && currentSensitivity) {
            if (msg.compute_key) {
                currentSensitivityComputeKey.current = msg.compute_key;
            }
            computeFailedRef.current = false;
            setIsComputing(true);
            setAuditPassed(null);
            computeStore.setComputeState({ isComputing: true, auditPassed: null, computeError: null });
        }

        // ── scenario_computed (compute done — pull data via REST) ──
        // audit_passed is delivered later by `scenario_audited` in the merged flow,
        // but we still read it here for back-compat if the field is present.
        if (msg.type === 'scenario_computed' && !currentSensitivity) {
            if (isStaleScenarioEvent(msg)) return;
            setIsComputing(false);
            setIsComputed(true);
            if (msg.audit_passed !== undefined) {
                setAuditPassed(msg.audit_passed);
            }
            computeStore.setComputeState({
                isComputing: false,
                isComputed: true,
                auditPassed: msg.audit_passed !== undefined ? msg.audit_passed : computeStore.auditPassed,
                computeError: null,
            });
            computeStore.incrementGeneration();
            refreshModel();
            setComputeGeneration(g => g + 1);
        }

        // ── scenario_audited (audit result delivered separately after compute+dashboard) ──
        if (msg.type === 'scenario_audited' && !currentSensitivity) {
            if (isStaleScenarioEvent(msg)) return;
            setAuditPassed(msg.audit_passed);
            computeStore.setComputeState({
                auditPassed: msg.audit_passed,
            });
        }

        // ── summary_computed — dashboard_computed was flipped to True in DB.
        // Re-fetch model so currentScenario/currentSensitivity.dashboard_computed
        // reflect the new value (the Dashboard page gates on this field).
        // No entity/stale guard: backend only emits this for the current compute
        // (via is_current_compute_key), and the event applies to the scenario or
        // any of its sensitivities — both of which live in the refreshed model.
        if (msg.type === 'summary_computed') {
            refreshModel();
        }

        // ── sensitivity_computed ──
        if (msg.type === 'sensitivity_computed' && currentSensitivity && currentSensitivity.id == msg.sensitivity_id) {
            if (isStaleSensitivityEvent(msg)) return;
            setIsComputing(false);
            setIsComputed(true);
            if (msg.audit_passed !== undefined) {
                setAuditPassed(msg.audit_passed);
            }
            computeStore.setComputeState({
                isComputing: false,
                isComputed: true,
                auditPassed: msg.audit_passed !== undefined ? msg.audit_passed : computeStore.auditPassed,
                computeError: null,
            });
            computeStore.incrementGeneration();
            refreshModel();
            setComputeGeneration(g => g + 1);
        }

        // ── sensitivity_audited ──
        if (msg.type === 'sensitivity_audited' && currentSensitivity && currentSensitivity.id == msg.sensitivity_id) {
            if (isStaleSensitivityEvent(msg)) return;
            setAuditPassed(msg.audit_passed);
            computeStore.setComputeState({
                auditPassed: msg.audit_passed,
            });
        }

        // ── scenario_input_errors (applies even when viewing a sensitivity — scenario errors block sensitivities too) ──
        if (msg.type === 'scenario_input_errors') {
            if (isStaleScenarioEvent(msg)) return;
            computeFailedRef.current = true;
            setIsComputing(false);
            computeStore.setComputeState({ isComputing: false, computeError: 'input_errors' });
            setErrorGeneration(g => g + 1);
        }

        // ── sensitivity_input_errors ──
        if (msg.type === 'sensitivity_input_errors' && currentSensitivity && currentSensitivity.id == msg.sensitivity_id) {
            if (isStaleSensitivityEvent(msg)) return;
            computeFailedRef.current = true;
            setIsComputing(false);
            computeStore.setComputeState({ isComputing: false, computeError: 'input_errors' });
            setErrorGeneration(g => g + 1);
        }

        // ── scenario_compute_error ──
        if (msg.type === 'scenario_compute_error' && !currentSensitivity) {
            if (isStaleScenarioEvent(msg)) return;
            computeFailedRef.current = true;
            setIsComputing(false);
            computeStore.setComputeState({ isComputing: false, computeError: 'compute_error' });
            setErrorGeneration(g => g + 1);
        }

        // ── sensitivity_compute_error ──
        if (msg.type === 'sensitivity_compute_error' && currentSensitivity && currentSensitivity.id == msg.sensitivity_id) {
            if (isStaleSensitivityEvent(msg)) return;
            computeFailedRef.current = true;
            setIsComputing(false);
            computeStore.setComputeState({ isComputing: false, computeError: 'compute_error' });
            setErrorGeneration(g => g + 1);
        }
    }, [lastJsonMessage]);

    // ── Stuck compute timeout — re-request state if no result after 2 minutes ──
    useEffect(() => {
        if (!isComputing) return;
        const timeout = setTimeout(() => {
            if (!currentSensitivity) {
                sendJsonMessage({ type: 'scenario_state_request' });
            } else {
                sendJsonMessage({ type: 'sensitivity_state_request', sensitivity_id: currentSensitivity.id });
            }
        }, 120_000);
        return () => clearTimeout(timeout);
    }, [isComputing, currentSensitivity, sendJsonMessage]);

    // Status bar extra props — set by FME editor for claimWrite, errors, etc.
    const [statusBarProps, setStatusBarProps] = useState<FMEStatusBarProps>({});

    // Context-level compute errors — fetched from API, available on all pages
    const [contextFirstError, setContextFirstError] = useState<string | null>(null);
    const [contextSensitivityErrorCount, setContextSensitivityErrorCount] = useState<number>(0);
    // Counter incremented on error WebSocket events to trigger re-fetch
    const [errorGeneration, setErrorGeneration] = useState(0);

    // Fetch scenario compute errors from API
    const fetchScenarioErrors = useCallback(async (scenarioId: number) => {
        try {
            const errors = await getComputeErrors(scenarioId);
            const scenarioErrors = errors.filter((e: any) => e.source !== 'sensitivity');
            setContextFirstError(scenarioErrors.length > 0 ? scenarioErrors[0].error : null);
        } catch { setContextFirstError(null); }
    }, []);

    // Fetch sensitivity compute errors from API
    const fetchSensitivityErrors = useCallback(async (sensitivityId: number) => {
        try {
            const errors = await getSensitivityComputeErrors(sensitivityId);
            setContextSensitivityErrorCount(errors.length);
        } catch { setContextSensitivityErrorCount(0); }
    }, []);

    // Fetch errors when scenario/sensitivity changes or on initial load
    useEffect(() => {
        if (!currentScenario?.id) return;
        fetchScenarioErrors(currentScenario.id);
        if (currentSensitivity?.id) {
            fetchSensitivityErrors(currentSensitivity.id);
        } else {
            setContextSensitivityErrorCount(0);
        }
    }, [currentScenario?.id, currentSensitivity?.id]);

    // Re-fetch errors after compute completes or fails
    useEffect(() => {
        if (!currentScenario?.id || (computeGeneration === 0 && errorGeneration === 0)) return;
        fetchScenarioErrors(currentScenario.id);
        if (currentSensitivity?.id) {
            fetchSensitivityErrors(currentSensitivity.id);
        }
    }, [computeGeneration, errorGeneration]);

    // Setters
    const setCurrentScenario = useCallback((scenario: any) => {
        setSelectedScenarioId(scenario.id);
        setSelectedSensitivityId(null); // Reset sensitivity when switching scenario
    }, []);

    const setCurrentSensitivity = useCallback((sensitivity: any) => {
        setSelectedSensitivityId(sensitivity?.id || null);
    }, []);

    return (
        <ModelContext.Provider value={{
            model,
            currentScenario,
            currentSensitivity,
            currentEntity,
            setCurrentScenario,
            setCurrentSensitivity,
            refreshModel,
            statusBarProps,
            setStatusBarProps,
            // Entity resolution state
            isEntityLoading,
            entityError,
            // WebSocket
            sendJsonMessage,
            lastJsonMessage,
            readyState,
            getWebSocket: getWebSocket as () => WebSocket | null,
            // Compute state
            isComputing,
            isComputed,
            auditPassed,
            computeFailedRef,
            computeGeneration,
            contextFirstError,
            contextSensitivityErrorCount,
        }}>
            {children}
        </ModelContext.Provider>
    );
}
